import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Pagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  ExpandMore,
  Clear,
  FilterList,
  Close,
} from "@mui/icons-material";
import ProductCard from "../components/common/ProductCard";
import { UIProduct } from "../types";
import { EntityStatus, EntityType } from "../types";
import type { ProductCatalogItem } from "../types";
import { apiService } from "../api/services/apiService";
import PageTitle from "../components/common/PageTitle";
import {
  collectUniqueTags,
  matchesTagFilter,
} from "../utils/explorerUtils";

const STATUS_OPTIONS = Object.values(EntityStatus).filter(
  (status) => status !== EntityStatus.DRAFT,
);
const UNCATEGORIZED_LABEL = "Uncategorized";

function catalogItemToUIProduct(p: ProductCatalogItem): UIProduct {
  const categoryNames = (p.categoryNames ?? []).filter(Boolean);
  return {
    id: p.uuid,
    name: p.name,
    description: p.description || "No description available",
    category: categoryNames[0] ?? "Uncategorized",
    categoryNames,
    tags: p.tags ?? [],
    heroImage: p.heroImageName ?? null,
    status: p.status as EntityStatus,
    type: p.type as EntityType,
    subscribable: p.subscribable === true,
  };
}

function productMatchesCategoryFilter(
  product: UIProduct,
  selectedCategories: string[],
): boolean {
  if (!selectedCategories.length) return true;
  const names =
    product.categoryNames.length > 0
      ? product.categoryNames
      : [UNCATEGORIZED_LABEL];
  return names.some((name) => selectedCategories.includes(name));
}

const ProductsCatalogPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<EntityStatus[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [subscribableOnly, setSubscribableOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<UIProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<UIProduct[]>([]);
  const [expandedFilters, setExpandedFilters] = useState<string[]>([
    "category",
    "status",
    "tags",
  ]);

  const pageSize = 9;

  const formatText = (text: string): string => {
    if (!text) return text;
    return text.trim();
  };

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiService.getProductCatalog();
      const uiProducts = list.map(catalogItemToUIProduct);
      setProducts(uiProducts);
      setFilteredProducts(uiProducts);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch products");
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const categories = useMemo(() => {
    const names = new Set<string>();
    for (const product of products) {
      if (product.categoryNames.length > 0) {
        product.categoryNames.forEach((n) => names.add(n));
      } else {
        names.add(UNCATEGORIZED_LABEL);
      }
    }
    return Array.from(names).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [products]);

  const tags = useMemo(() => collectUniqueTags(products), [products]);

  useEffect(() => {
    let filtered = products;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(query) ||
          (product.description || "").toLowerCase().includes(query) ||
          product.categoryNames.some((c) => c.toLowerCase().includes(query)) ||
          (product.category || "").toLowerCase().includes(query) ||
          product.tags.some((tag) => tag.toLowerCase().includes(query)),
      );
    }

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((product) =>
        productMatchesCategoryFilter(product, selectedCategories),
      );
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((product) =>
        selectedStatuses.includes(product.status),
      );
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter((product) =>
        matchesTagFilter(product, selectedTags),
      );
    }

    if (subscribableOnly) {
      filtered = filtered.filter((product) => product.subscribable === true);
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [
    searchQuery,
    selectedCategories,
    selectedStatuses,
    selectedTags,
    subscribableOnly,
    products,
  ]);

  const handleFilterToggle = (filter: string) => {
    setExpandedFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter],
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const handleSelectAllCategories = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories([...categories]);
    }
  };

  const handleStatusToggle = (status: EntityStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const handleSelectAllStatuses = () => {
    if (selectedStatuses.length === STATUS_OPTIONS.length) {
      setSelectedStatuses([]);
    } else {
      setSelectedStatuses([...STATUS_OPTIONS]);
    }
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleSelectAllTags = () => {
    if (selectedTags.length === tags.length) {
      setSelectedTags([]);
    } else {
      setSelectedTags([...tags]);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSelectedTags([]);
    setSubscribableOnly(false);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeCategoryFilter = (category: string) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== category));
  };

  const removeStatusFilter = (status: EntityStatus) => {
    setSelectedStatuses((prev) => prev.filter((s) => s !== status));
  };

  const removeTagFilter = (tag: string) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tag));
  };

  const totalPages = Math.ceil(filteredProducts.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProducts = filteredProducts.slice(
    startIndex,
    startIndex + pageSize,
  );

  const activeFiltersCount =
    (searchQuery ? 1 : 0) +
    selectedCategories.length +
    selectedStatuses.length +
    selectedTags.length +
    (subscribableOnly ? 1 : 0);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Product Catalog"
        pageDescription="Browse our comprehensive collection of Products, APIs, and AI agents."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Left Sidebar - Filters */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
                position: "sticky",
                top: 100,
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FilterList
                    sx={{ mr: 1, color: "primary.main", fontSize: 20 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Filters
                  </Typography>
                </Box>
                {activeFiltersCount > 0 && (
                  <Chip
                    label={`${activeFiltersCount} active`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search products..."
                  variant="outlined"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>

              {/* Category Filter Accordion */}
              <Accordion
                expanded={expandedFilters.includes("category")}
                onChange={() => handleFilterToggle("category")}
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: 700 }}>
                    Category ({categories.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleSelectAllCategories}
                      sx={{ textTransform: "none", fontSize: "0.875rem" }}
                    >
                      {selectedCategories.length === categories.length
                        ? "Deselect all"
                        : "Select all"}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      pr: 1,
                      mr: -0.5,
                    }}
                  >
                    {categories.map((category) => (
                      <FormControlLabel
                        key={category}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedCategories.includes(category)}
                            onChange={() => handleCategoryToggle(category)}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "visible",
                              textOverflow: "unset",
                              lineHeight: 1.5,
                              textIndent: "2px",
                              display: "inline",
                            }}
                          >
                            {formatText(category) || "Uncategorized"}
                          </Typography>
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 0.5,
                          ml: 0,
                          mr: 0.5,
                        }}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Status Filter Accordion */}
              <Accordion
                expanded={expandedFilters.includes("status")}
                onChange={() => handleFilterToggle("status")}
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: 700 }}>
                    Status ({STATUS_OPTIONS.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleSelectAllStatuses}
                      sx={{ textTransform: "none", fontSize: "0.875rem" }}
                    >
                      {selectedStatuses.length === STATUS_OPTIONS.length
                        ? "Deselect all"
                        : "Select all"}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      pr: 1,
                      mr: -0.5,
                    }}
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <FormControlLabel
                        key={status}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedStatuses.includes(status)}
                            onChange={() => handleStatusToggle(status)}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                            {status}
                          </Typography>
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 0.5,
                          ml: 0,
                          mr: 0.5,
                        }}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Tags Filter Accordion */}
              <Accordion
                expanded={expandedFilters.includes("tags")}
                onChange={() => handleFilterToggle("tags")}
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: 700 }}>
                    Tags ({tags.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {tags.length > 0 ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={handleSelectAllTags}
                          sx={{ textTransform: "none", fontSize: "0.875rem" }}
                        >
                          {selectedTags.length === tags.length
                            ? "Deselect all"
                            : "Select all"}
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          maxHeight: 200,
                          overflowY: "auto",
                          pr: 1,
                          mr: -0.5,
                        }}
                      >
                        {tags.map((tag) => (
                          <FormControlLabel
                            key={tag}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedTags.includes(tag)}
                                onChange={() => handleTagToggle(tag)}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ lineHeight: 1.5 }}
                              >
                                {tag}
                              </Typography>
                            }
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                              ml: 0,
                              mr: 0.5,
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No tags found on products.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              <FormControlLabel
                sx={{ mt: 2, ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={subscribableOnly}
                    onChange={(e) => setSubscribableOnly(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">Available to subscribe</Typography>
                }
              />

              <Button
                fullWidth
                variant="outlined"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                disabled={activeFiltersCount === 0}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                Clear all filters
              </Button>
            </Paper>
          </Grid>

          {/* Right Content - Products */}
          <Grid item xs={12} md={9}>
            {activeFiltersCount > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                  bgcolor: "grey.50",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Active filters:
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Close />}
                    onClick={handleClearFilters}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    Clear all
                  </Button>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {searchQuery && (
                    <Chip
                      label={`Search: "${searchQuery}"`}
                      onDelete={() => setSearchQuery("")}
                      size="small"
                      deleteIcon={<Close fontSize="small" />}
                    />
                  )}
                  {selectedCategories.map((category) => (
                    <Chip
                      key={category}
                      label={`Category: ${
                        formatText(category) || "Uncategorized"
                      }`}
                      onDelete={() => removeCategoryFilter(category)}
                      size="small"
                      deleteIcon={<Close fontSize="small" />}
                    />
                  ))}
                  {selectedStatuses.map((status) => (
                    <Chip
                      key={status}
                      label={`Status: ${status}`}
                      onDelete={() => removeStatusFilter(status)}
                      size="small"
                      deleteIcon={<Close fontSize="small" />}
                    />
                  ))}
                  {selectedTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={`Tag: ${tag}`}
                      onDelete={() => removeTagFilter(tag)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      deleteIcon={<Close fontSize="small" />}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 400,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 4 }}>
                {error}
              </Alert>
            )}

            {!loading && !error && (
              <>
                {paginatedProducts.length > 0 ? (
                  <>
                    <Grid container spacing={3}>
                      {paginatedProducts.map((product) => (
                        <Grid item xs={12} sm={6} md={4} key={product.id}>
                          <ProductCard product={product} />
                        </Grid>
                      ))}
                    </Grid>

                    {totalPages > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 6,
                          pt: 4,
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          color="primary"
                          size="medium"
                          showFirstButton
                          showLastButton
                          siblingCount={1}
                          boundaryCount={1}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 6,
                      textAlign: "center",
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "grey.300",
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      No products found matching your criteria
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Try adjusting your filters or search term
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleClearFilters}
                      startIcon={<Clear />}
                    >
                      Clear all filters
                    </Button>
                  </Paper>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductsCatalogPage;
